import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { Mail, Lock, AlertCircle, User, Eye, EyeOff, CheckCircle, X, Shield, Check, ArrowRight } from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface SellerForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  storeName: string;
  storeDescription: string;
  storeCategories: string[];
  address: string;
  subscription: 'free' | 'premium';
  enableNegotiation: boolean;
  idFrontDocument: File | null;
  idBackDocument: File | null;
  proofOfAddress: File | null;
  selfieWithId: File | null;
}

export function RegisterPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [existingApplication, setExistingApplication] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<'buyer' | 'seller' | 'delivery'>('buyer')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  
  // Password strength criteria
  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  })

  useEffect(() => {
    if (user) {
      navigate('/')
    }
  }, [user, navigate])

  useEffect(() => {
    // Check password strength
    setPasswordCriteria({
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password)
    })
  }, [password])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate minimum password requirements
    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setError('')
    setLoading(true)

    try {      
      // Register without email confirmation
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role
          }
        }
      })

      if (error) throw error

      if (data.user && data.user.identities && data.user.identities.length === 0) {
        setError('Cet email est déjà utilisé. Veuillez vous connecter ou utiliser un autre email.')
        setLoading(false)
        return
      }

      // Show success message instead of waiting for email
      setRegistrationSuccess(true)
      setToastMessage('Inscription réussie!')
      setToastType('success')
      setShowToast(true)
      
    } catch (err) {
      console.error('Registration error:', err)
      setError('Erreur lors de la création du compte')
    } finally {
      setLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Calculate password strength
  const getPasswordStrength = () => {
    const criteria = Object.values(passwordCriteria);
    const trueCount = criteria.filter(Boolean).length;
    
    if (trueCount <= 1) return { strength: 'weak', color: 'bg-red-500', width: '20%' };
    if (trueCount <= 3) return { strength: 'medium', color: 'bg-yellow-500', width: '60%' };
    return { strength: 'strong', color: 'bg-green-500', width: '100%' };
  }

  const passwordStrength = getPasswordStrength();

  if (registrationSuccess) {
    return (
      <div 
        className="fixed inset-0 flex flex-col justify-center items-center bg-cover bg-center px-4 py-6"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&q=80')",
          backgroundColor: "#0056b3",
          backgroundBlendMode: "overlay",
          backgroundSize: "cover"
        }}
      >
        <div className="bg-white/90 backdrop-blur-sm py-8 px-4 shadow-xl shadow-primary/20 sm:rounded-xl sm:px-10 border border-white/20 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Inscription réussie!</h2>
            <p className="mt-2 text-gray-600">
              Votre compte a été créé avec succès. Vous pouvez maintenant vous connecter avec votre email et mot de passe.
            </p>
            <div className="mt-6">
              <Link to="/login" className="inline-flex items-center justify-center w-full px-5 py-3 text-base font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors duration-200">
                Se connecter maintenant
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <p className="mt-4 text-sm text-gray-500">
                Vous pouvez également vous connecter via notre application mobile AEM Marketplace.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="fixed inset-0 flex flex-col justify-center items-center bg-cover bg-center px-4 py-6 overflow-y-auto"
      style={{ 
        backgroundImage: "url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&q=80')",
        backgroundColor: "#0056b3",
        backgroundBlendMode: "overlay",
        backgroundSize: "cover"
      }}
    >
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="sm:mx-auto sm:w-full sm:max-w-md mb-6">
        <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-white">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-white/80">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-white hover:text-white/80">
            Sign in
          </Link>
        </p>
      </div>

      <div className="w-full max-w-md px-4">
        <div className="bg-white/90 backdrop-blur-sm py-5 sm:py-8 px-4 shadow-xl shadow-primary/20 sm:rounded-xl sm:px-10 border border-white/20">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Important Notice */}
          <div className="mb-5 bg-yellow-50 border border-yellow-200 rounded-md p-3 sm:p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Shield className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Attention important!</h3>
                <div className="mt-1 text-sm text-yellow-700">
                  <p>Veuillez noter avec soin votre email et mot de passe. Beaucoup d'utilisateurs font des erreurs lors de l'inscription.</p>
                  <ul className="list-disc mt-1 pl-5 space-y-0.5 text-xs">
                    <li>Vérifiez l'orthographe de votre email</li>
                    <li>Créez un mot de passe que vous pourrez vous rappeler</li>
                    <li>Notez vos identifiants dans un endroit sûr</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                type="email"
                id="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full pl-3 pr-3 py-2 sm:py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 sm:py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  minLength={6}
                  placeholder="Combine letters, numbers & symbols"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Password strength indicator */}
              {password.length > 0 && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className={`h-1.5 rounded-full ${passwordStrength.color}`} 
                      style={{ width: passwordStrength.width }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 flex flex-wrap items-center justify-between">
                    <p className="mb-1">Password strength: <span className="font-medium">{passwordStrength.strength}</span></p>
                    <div>
                      <ul className="space-y-0.5 mt-1 text-xs">
                        <li className="flex items-center">
                          {passwordCriteria.minLength ? 
                            <Check className="h-3 w-3 text-green-500 mr-1" /> : 
                            <X className="h-3 w-3 text-red-500 mr-1" />}
                          <span>At least 6 characters</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                I want to
              </label>
              <div className="mt-1 grid grid-cols-3 gap-3">
                <button
                  type="button"
                  className={`${
                    role === 'buyer'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } col-span-1 py-2 px-3 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                  onClick={() => setRole('buyer')}
                >
                  Buy
                </button>
                <button
                  type="button"
                  className={`${
                    role === 'seller'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } col-span-1 py-2 px-3 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                  onClick={() => setRole('seller')}
                >
                  Sell
                </button>
                <button
                  type="button"
                  className={`${
                    role === 'delivery'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } col-span-1 py-2 px-3 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                  onClick={() => setRole('delivery')}
                >
                  Deliver
                </button>
              </div>
            </div>

            <div className="mt-1">
              <Button
                type="submit"
                variant="primary"
                className="w-full py-3 flex justify-center"
                disabled={loading}
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Create account'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}